import { useMemo, useCallback, memo, useState } from "react"
import { useTranslation } from "react-i18next"
import { RiSideBarLine } from "@remixicon/react"
import {
  RiFileTextLine,
  RiGridLine,
  RiBarChartHorizontalLine,
  RiLineChartLine,
  RiBarChartGroupedLine,
  RiBubbleChartLine,
  RiTimeLine,
  RiTableLine,
  RiMapPinLine,
  RiPieChartLine,
  RiExpandHeightLine,
  RiAddLine,
} from "@remixicon/react"

import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import type { PaletteItem } from "@/types/pdfBuilder"
import {
  buildItemsFromPalette,
  PALETTE_DRAG_MIME,
} from "@/app/report/pdf/builder/utils/paletteItems"

const PALETTE_ITEMS: PaletteItem[] = [
  {
    type: "activityInfo",
    label: "Activity Info",
    icon: "activityInfo",
    defaultWidth: 12,
    // 격자 한 칸 = 40px, 아이템 높이 = h * 40 - 10.
    // 내용(로고·이름·메타 한 줄)은 130px 이라 3(110px)이면 아래가 잘린다.
    defaultHeight: 4,
    minWidth: 3,
    minHeight: 2,
    description: "Basic information about this activity",
  },
  {
    type: "summary",
    label: "Summary",
    icon: "summary",
    defaultWidth: 12,
    // 리포트 화면과 같은 DailyReportSummary 카드(빌더 여백 12·제목 16·값 24·단위 12·지표 11·간격 6×3)는
    // 약 105px 이 든다. 3행(100px)은 잘리고 4행(130px)이면 지표명이 두 줄이어도 들어간다 (#75).
    defaultHeight: 4,
    minWidth: 3,
    minHeight: 4,
    description: "Display key metrics as cards",
  },
  {
    // 트렌드 전용 — 기간 안의 액티비티를 한 줄씩 (2026-10-02 요청)
    type: "activityGraph",
    label: "Chart by Activity",
    icon: "combo",
    defaultWidth: 12,
    defaultHeight: 5,
    minWidth: 3,
    minHeight: 3,
    description: "Per-activity chart over the selected period",
  },
  {
    // 리포트 Graph 섹션과 같은 선수별 차트 — 지표(최대 3)마다 막대/누적/점/선/면을 고른다 (2026-09-28: Bar·Line·Point·Combo 통합)
    type: "combo",
    label: "Chart by Player",
    icon: "combo",
    defaultWidth: 12,
    defaultHeight: 5,
    minWidth: 3,
    minHeight: 3,
    description: "Per-player chart, bar/line/point per metric like the report graph",
  },
  {
    // 리포트 Scatter 섹션 — 선수마다 두 지표를 점으로 (#75 5단계-b)
    type: "scatter",
    label: "Scatter Chart",
    icon: "scatter",
    defaultWidth: 6,
    defaultHeight: 9,
    minWidth: 4,
    minHeight: 6,
    description: "Two metrics per player, like the report scatter",
  },
  {
    type: "table",
    label: "Table",
    icon: "table",
    defaultWidth: 12,
    defaultHeight: 5,
    minWidth: 3,
    minHeight: 3,
    description: "Display data in tabular format",
  },
  {
    // 리포트 Tactic Map 섹션 — 맵과 액션 표를 한 아이템에 나란히 그린다 (2026-09-29 개편). 표는 옵션으로 끌 수 있다
    type: "map",
    // 번역 키가 key 로 만들어진다(chartSection<key>) — 배포된 번역의 "Map" 을 피하려고 새 키를 쓴다. 번역이 없으면 label 이 그대로 보인다
    key: "actionMap",
    label: "Action Map",
    icon: "map",
    // 새로 추가하면 페이지 폭의 절반(6/12칸)·높이 30% 남짓(9행 = 350px ≈ A4 1123px 의 31%) (2026-10-02 요청)
    defaultWidth: 6,
    defaultHeight: 9,
    minWidth: 4,
    minHeight: 6,
    // 새로 추가하면 액션 표는 꺼진 상태로 — 맵만 쓰는 경우가 많다 (2026-10-02 요청).
    // Default 배치(리포트 템플릿 구성)는 리포트 화면과 같게 표를 켠 채로 둔다
    presetOptions: { showActionTable: false },
    description: "Action map with its action table (formation, heatmap, zones, trajectory)",
  },
  {
    // 리포트 Session Details 섹션 — 한 항목이 세션·1분·5분·15분 네 차트를 만든다 (설정 공유, 크기는 개별) (2026-09-28)
    type: "sessionDetails",
    label: "Session Details",
    icon: "sessionDetails",
    defaultWidth: 12,
    defaultHeight: 6,
    minWidth: 6,
    minHeight: 4,
    description: "Session, 1/5/15 min charts from the report's Session Details",
  },
  // Comparison 은 아직 실데이터 검증 전이라 당분간 목록에서 뺀다.
  // 렌더러·옵션 패널·타입은 그대로 두었으니 아래 항목만 되살리면 다시 쓸 수 있다.
  // {
  //   type: "comparison",
  //   label: "Comparison",
  //   icon: "comparison",
  //   defaultWidth: 6,
  //   defaultHeight: 5,
  //   minWidth: 3,
  //   minHeight: 3,
  //   description: "Compare player proportions (pie, radar, treemap)",
  // },
  {
    type: "spacer",
    label: "Spacer",
    icon: "spacer",
    defaultWidth: 12,
    defaultHeight: 2,
    minWidth: 3,
    minHeight: 1,
    description: "Empty space between components (not printed)",
  },
]

function getIconComponent(iconType: string) {
  switch (iconType) {
    case "activityInfo":
      return RiFileTextLine
    case "summary":
      return RiGridLine
    case "bar":
      return RiBarChartHorizontalLine
    case "line":
      return RiLineChartLine
    case "combo":
      return RiBarChartGroupedLine
    case "point":
    case "scatter":
      return RiBubbleChartLine
    case "sessionDetails":
      return RiTimeLine
    case "table":
      return RiTableLine
    case "map":
      return RiMapPinLine
    case "comparison":
      return RiPieChartLine
    case "spacer":
      return RiExpandHeightLine
    default:
      return RiFileTextLine
  }
}

interface DraggablePaletteItemProps {
  item: PaletteItem
  onClickAdd: (item: PaletteItem) => void
}

const DraggablePaletteItem = memo(function DraggablePaletteItem({
  item,
  onClickAdd,
}: DraggablePaletteItemProps) {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)

  const paletteKey = item.key ?? item.type
  // 번역 키: chartSection + 키의 파스칼 표기 (예: sessionDetails-5m → chartSectionSessionDetails5m)
  const labelKey = paletteKey
    .replace(/-(\w)/g, (_, c: string) => c.toUpperCase())
    .replace(/^\w/, (c) => c.toUpperCase())

  // 브라우저 기본(HTML5) 드래그 — 라이브러리 센서(dnd-kit)는 실제 Chrome 에서 활성화되지 않는 경우가 있어
  // (2026-09-28 Mac Chrome 보고: 잔상도 안 따라옴) 팔레트 카드는 native drag 로 옮긴다.
  // 어떤 카드인지는 스토어(draggedPaletteItem)와 dataTransfer 둘 다에 싣고, 캔버스(ReportCanvas)가 drop 을 받는다.
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData(PALETTE_DRAG_MIME, paletteKey)
    e.dataTransfer.setData("text/plain", item.label)
    e.dataTransfer.effectAllowed = "copy"
    usePdfBuilderStore.getState().setDraggingFromPalette(true, item)
    setIsDragging(true)
  }
  const handleDragEnd = () => {
    usePdfBuilderStore.getState().setDraggingFromPalette(false)
    setIsDragging(false)
  }

  const IconComponent = getIconComponent(item.icon)

  return (
    <div
      className={`ChartPalette__item ${isDragging ? "ChartPalette__item--dragging" : ""}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      role="button"
      tabIndex={0}
      aria-roledescription="draggable">
      <div className="ChartPalette__item-icon">
        <IconComponent size={22} />
      </div>
      <div className="ChartPalette__item-info">
        <span className="ChartPalette__item-label">{t(`chartSection${labelKey}`, item.label)}</span>
        {item.description && (
          <span className="ChartPalette__item-desc">
            {t(`chartSectionDesc${labelKey}`, item.description)}
          </span>
        )}
      </div>
      <button
        className="ChartPalette__item-add"
        onClick={(e) => {
          e.stopPropagation()
          onClickAdd(item)
        }}
        title={t("button.add", "Add")}>
        <RiAddLine size={20} />
      </button>
    </div>
  )
})

/** 트렌드 리포트에서 쓸 수 있는 컴포넌트 — 트렌드 템플릿 섹션(Chart by Activity·Chart by Player·Table) 기준 (2026-10-02 요청) */
const TREND_PALETTE_TYPES = new Set(["activityGraph", "combo", "table", "spacer"])

/** 액티비티 리포트에는 없는 컴포넌트 — 트렌드 템플릿에만 있는 섹션 (2026-10-02 요청) */
const TREND_ONLY_PALETTE_TYPES = new Set(["activityGraph"])

export default function ChartPalette({ onHide }: { onHide?: () => void }) {
  const { t } = useTranslation()

  // 트렌드 리포트는 쓸 수 있는 컴포넌트가 다르다 (2026-10-02 요청)
  const reportKind = usePdfBuilderStore((s) => s.filterReportKind)
  const paletteItems = useMemo(
    () =>
      reportKind === "trend"
        ? PALETTE_ITEMS.filter((item) => TREND_PALETTE_TYPES.has(item.type))
        : PALETTE_ITEMS.filter((item) => !TREND_ONLY_PALETTE_TYPES.has(item.type)),
    [reportKind],
  )

  const handleClickAdd = useCallback((item: PaletteItem) => {
    const { currentPageId, addItem } = usePdfBuilderStore.getState()
    if (!currentPageId) return
    // 캔버스 드롭과 같은 규칙(buildItemsFromPalette) — Session Details 는 네 아이템
    buildItemsFromPalette(item, 0, Infinity).forEach((newItem) => addItem(currentPageId, newItem))
  }, [])

  return (
    <div className="ChartPalette">
      <div className="ChartPalette__header">
        <h3>{t("palette", "Components")}</h3>
        {/* 패널 숨기기 — 다시 보이기는 왼쪽 가장자리 띠의 버튼 (2026-10-01 요청) */}
        {onHide && (
          <button
            className="ChartPalette__hide-btn"
            onClick={onHide}
            title={t("hidePanel", "Hide panel")}>
            <RiSideBarLine size={18} />
          </button>
        )}
      </div>
      <div className="ChartPalette__content">
        {paletteItems.map((item) => (
          <DraggablePaletteItem
            key={item.key ?? item.type}
            item={item}
            onClickAdd={handleClickAdd}
          />
        ))}
      </div>
    </div>
  )
}

export { PALETTE_ITEMS, getIconComponent }
export type { PaletteItem }
